import type { RealtimeChannel } from '@supabase/supabase-js';
import type { CompanyRole } from '../companies/types';
import type { User } from '../auth/types';
import type { OutboxItem, OutboxStatus } from '../sync/types';
import type {
  CreateOrderPayload,
  CreateWorkOrderItemPayload,
  OrderStatus,
  VisitResultSummary,
  WorkOrder,
  WorkOrderHelper,
  WorkOrderItem,
} from './types';
import { isVisitClosedStatus } from './statusUtils';
import { isSupabaseConfigured, supabase } from '../auth/supabaseClient';
import { assertTenantMatch } from './tenantAssertion';
import { db } from '../sync/db';
import { DEFAULT_COMPANY_ID, DEFAULT_TEMPLATE_ID, PILOT_TEMPLATE_IDS, getSeedTemplateByWorkType } from '../../shared/constants';
import { todayYMD } from '../../shared/utils/date';
import { makeId } from '../../shared/utils/ids';
import { getInitials } from '../../shared/utils/user';

const ACTIVE_OUTBOX_STATUSES: OutboxStatus[] = ['pending', 'processing', 'failed'];

function isOrderRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapFetchedOrderRows(rows: unknown[] | null | undefined): WorkOrder[] {
  return (rows ?? []).filter(isOrderRecord).map((row) => mapOrder(row));
}

/**
 * P0-1 stage 2: map a work_order_items row from Supabase into our domain
 * type. Tolerates partial / legacy shapes — anything missing defaults
 * sensibly so screens never crash on a row written by an older client.
 */
function mapItem(row: Record<string, unknown>): WorkOrderItem {
  const summary = row.result_summary;
  return {
    id: String(row.id),
    work_order_id: String(row.work_order_id),
    work_type: (row.work_type as WorkOrderItem['work_type']) ?? 'maintenance',
    service_type_id: row.service_type_id ? String(row.service_type_id) : null,
    checklist_template_id: row.checklist_template_id ? String(row.checklist_template_id) : null,
    title: typeof row.title === 'string' ? row.title : '',
    status: (row.status as WorkOrderItem['status']) ?? 'pending',
    outcome: (row.outcome as WorkOrderItem['outcome']) ?? null,
    outcome_reason: row.outcome_reason ? String(row.outcome_reason) : null,
    result_summary: (summary as VisitResultSummary | null) ?? null,
    price_planned: row.price_planned !== null && row.price_planned !== undefined
      ? Number(row.price_planned)
      : null,
    price_amount: typeof row.price_amount === 'number'
      ? row.price_amount
      : Number(row.price_amount ?? 0) || 0,
    price_adjustment_reason: row.price_adjustment_reason ? String(row.price_adjustment_reason) : null,
    started_at: row.started_at ? String(row.started_at) : null,
    completed_at: row.completed_at ? String(row.completed_at) : null,
    position: typeof row.position === 'number'
      ? row.position
      : Number(row.position ?? 0) || 0,
    created_at: String(row.created_at ?? new Date().toISOString()),
    updated_at: String(row.updated_at ?? row.created_at ?? new Date().toISOString()),
  };
}

/**
 * P0-1 stage 2: given a list of orders just loaded from work_orders, do
 * one batched fetch from work_order_items to attach `items` to each
 * order. Designed to be called once per dispatcher/tech list load.
 *
 * Backward-compatible:
 *  • If work_order_items returns nothing (e.g. environment hasn't applied
 *    stage 1 migration yet), orders come back with `items: []` — the UI
 *    falls back to the legacy single-service rendering through
 *    synthesiseLegacyItem() in itemsUtils.
 *  • If the table doesn't exist, the supabase call returns an error;
 *    we swallow it and return orders unchanged so the dispatcher screen
 *    keeps working even on a partially-migrated database.
 */
async function hydrateOrdersWithItems(orders: WorkOrder[]): Promise<WorkOrder[]> {
  if (orders.length === 0) return orders;
  if (!isSupabaseConfigured) return orders;

  const orderIds = orders.map((o) => o.id);
  const { data, error } = await supabase
    .from('work_order_items')
    .select('*')
    .in('work_order_id', orderIds)
    .order('position', { ascending: true });

  if (error) {
    console.warn(
      `[Кворум] hydrateOrdersWithItems failed (${error.message}). ` +
        'Returning orders without items; UI will fall back to legacy fields.',
    );
    return orders;
  }

  const byOrder = new Map<string, WorkOrderItem[]>();
  for (const row of data ?? []) {
    if (!isOrderRecord(row)) continue;
    const mapped = mapItem(row);
    const arr = byOrder.get(mapped.work_order_id);
    if (arr) arr.push(mapped);
    else byOrder.set(mapped.work_order_id, [mapped]);
  }

  return orders.map((order) => ({
    ...order,
    items: byOrder.get(order.id) ?? [],
  }));
}

/**
 * P0-4 — batch-load helper assignments for a list of orders. Mirrors
 * hydrateOrdersWithItems shape so we can chain them in fetchers.
 *
 * Helpers are looked up by work_order_id; user names come from a
 * separate users-table join so the UI can show "Маша · Петя" without
 * a second round trip per order.
 *
 * Graceful: if the work_order_assignees table doesn't exist yet (e.g.
 * SQL migration hasn't been applied), we log and return orders
 * untouched so the rest of the app keeps working.
 */
async function hydrateOrdersWithHelpers(orders: WorkOrder[]): Promise<WorkOrder[]> {
  if (orders.length === 0) return orders;
  if (!isSupabaseConfigured) return orders;

  const orderIds = orders.map((o) => o.id);
  // No user_profiles join here — the FK alias name is not portable across
  // schemas, and the UI already resolves display names via useTeam() on
  // the client. We just return the raw user_id and let the dispatcher /
  // admin / technician screens look up names themselves.
  const { data, error } = await supabase
    .from('work_order_assignees')
    .select('id, work_order_id, user_id, created_at')
    .in('work_order_id', orderIds);

  if (error) {
    console.warn(
      `[Кворум] hydrateOrdersWithHelpers failed (${error.message}). ` +
        'Returning orders without helpers; assume solo-tech.',
    );
    return orders;
  }

  const byOrder = new Map<string, WorkOrderHelper[]>();
  for (const row of data ?? []) {
    if (!isOrderRecord(row)) continue;
    const helper: WorkOrderHelper = {
      id: String(row.id),
      work_order_id: String(row.work_order_id),
      user_id: String(row.user_id),
      user_name: null, // resolved client-side via team list
      created_at: String(row.created_at ?? new Date().toISOString()),
    };
    const arr = byOrder.get(helper.work_order_id);
    if (arr) arr.push(helper);
    else byOrder.set(helper.work_order_id, [helper]);
  }

  return orders.map((order) => ({
    ...order,
    helpers: byOrder.get(order.id) ?? [],
  }));
}

/** Convenience: hydrate items AND helpers in two parallel calls. */
async function hydrateOrders(orders: WorkOrder[]): Promise<WorkOrder[]> {
  const [withItems, withHelpers] = await Promise.all([
    hydrateOrdersWithItems(orders),
    hydrateOrdersWithHelpers(orders),
  ]);
  // Merge — withItems and withHelpers each contain the original order's
  // fields plus their respective decoration. Combine by id.
  const helpersById = new Map(withHelpers.map((o) => [o.id, o.helpers ?? []]));
  return withItems.map((o) => ({ ...o, helpers: helpersById.get(o.id) ?? [] }));
}

function mapOrder(row: Record<string, unknown>): WorkOrder {
  return {
    id: String(row.id),
    company_id: String(row.company_id),
    client_name: String(row.client_name),
    client_phone: String(row.client_phone),
    address: String(row.address),
    floor: String(row.floor ?? ''),
    location_kind: String(row.location_kind ?? ''),
    customer_id: row.customer_id ? String(row.customer_id) : null,
    crew_id: row.crew_id ? String(row.crew_id) : null,
    object_id: row.object_id ? String(row.object_id) : null,
    assigned_to: row.assigned_to ? String(row.assigned_to) : null,
    created_by: String(row.created_by),
    work_type: row.work_type as WorkOrder['work_type'],
    service_type_id: row.service_type_id ? String(row.service_type_id) : null,
    checklist_template_id: row.checklist_template_id ? String(row.checklist_template_id) : DEFAULT_TEMPLATE_ID,
    priority: row.priority as WorkOrder['priority'],
    scheduled_date: String(row.scheduled_date),
    time_from: String(row.time_from),
    time_to: String(row.time_to),
    status: row.status as WorkOrder['status'],
    started_at: row.started_at ? String(row.started_at) : null,
    completed_at: row.completed_at ? String(row.completed_at) : null,
    created_at: String(row.created_at),
    updated_at: String(row.updated_at ?? row.created_at),
    description: String(row.description ?? ''),
    failure_reason: row.failure_reason ? String(row.failure_reason) : null,
    result_summary: (row.result_summary as VisitResultSummary | null) ?? null,
    repeat_of: row.repeat_of ? String(row.repeat_of) : null,
    dismissed_attention: row.dismissed_attention === true ? true : false,
    // 1.13.0 — JSONB поле приходит уже как объект из supabase-js, не строкой.
    // Если null или невалидно — храним null.
    dismissal_request: (row.dismissal_request as unknown) && typeof row.dismissal_request === 'object'
      ? (row.dismissal_request as WorkOrder['dismissal_request'])
      : null,
    help_requested: row.help_requested === true ? true : null,
    help_comment: row.help_comment ? String(row.help_comment) : null,
  };
}

function sortOrders(orders: WorkOrder[]) {
  return orders
    .slice()
    .sort((a, b) => {
      const createdDiff = b.created_at.localeCompare(a.created_at);
      if (createdDiff !== 0) return createdDiff;

      const scheduledDiff = `${a.scheduled_date} ${a.time_from}`.localeCompare(`${b.scheduled_date} ${b.time_from}`);
      if (scheduledDiff !== 0) return scheduledDiff;

      return a.id.localeCompare(b.id);
    });
}

async function mergeRemoteWithLocal(remote: WorkOrder): Promise<WorkOrder> {
  const local = await db.orders.get(remote.id);
  if (!local) return remote;

  const pendingOutbox = await db.outbox.where('order_id').equals(remote.id).count();
  const session = await db.visit_sessions.get(remote.id);
  const hasUnsyncedProgress =
    pendingOutbox > 0 ||
    Boolean(session?.completed_at && !isVisitClosedStatus(remote.status)) ||
    Boolean(session?.started_at && remote.status === 'assigned');

  if (!hasUnsyncedProgress) {
    return {
      ...remote,
      result_summary: remote.result_summary ?? local.result_summary,
      help_requested: remote.help_requested ?? local.help_requested ?? null,
      help_comment: remote.help_comment ?? local.help_comment ?? null,
      // 1.13.0 — server-authoritative: workflow одобрения всегда требует
      // подключения, локального оффлайн-черновика не бывает.
      dismissal_request: remote.dismissal_request ?? local.dismissal_request ?? null,
      // P0-1 stage 2: prefer freshly hydrated items, but fall back to whatever
      // Dexie cached if the server load couldn't read them (RLS/migration
      // partially applied).
      items: remote.items ?? local.items,
    };
  }

  return {
    ...remote,
    status: local.status,
    started_at: local.started_at ?? remote.started_at,
    completed_at: local.completed_at ?? remote.completed_at,
    updated_at: local.updated_at ?? remote.updated_at,
    result_summary: local.result_summary ?? remote.result_summary,
    help_requested: local.help_requested ?? remote.help_requested ?? null,
    help_comment: local.help_comment ?? remote.help_comment ?? null,
    dismissal_request: remote.dismissal_request ?? local.dismissal_request ?? null,
    // P0-1 stage 2: items are server-authoritative — local copy is for
    // offline read-only display. We trust whatever remote hydration produced.
    items: remote.items ?? local.items,
  };
}

async function getPendingCreatedOrderIds() {
  const outbox = await db.outbox.toArray();
  return new Set(
    outbox
      .filter((item) => item.type === 'create_order' && ACTIVE_OUTBOX_STATUSES.includes(item.status))
      .map((item) => item.order_id),
  );
}

async function mergeFetchedOrders(remoteOrders: WorkOrder[], options?: { technicianId?: string }) {
  const mergedRemote = await Promise.all(remoteOrders.map((order) => mergeRemoteWithLocal(order)));
  const remoteById = new Map(mergedRemote.map((order) => [order.id, order]));
  const pendingCreatedOrderIds = await getPendingCreatedOrderIds();

  if (pendingCreatedOrderIds.size > 0) {
    const localOrders = await db.orders.toArray();
    for (const localOrder of localOrders) {
      if (!pendingCreatedOrderIds.has(localOrder.id)) continue;
      if (options?.technicianId && localOrder.assigned_to !== options.technicianId) continue;
      if (!remoteById.has(localOrder.id)) {
        remoteById.set(localOrder.id, localOrder);
      }
    }
  }

  const merged = sortOrders(Array.from(remoteById.values()));
  await db.orders.bulkPut(merged);
  return merged;
}

async function triggerOutboxFlush() {
  const { flushOutbox } = await import('../sync/outboxProcessor');
  await flushOutbox();
}

/**
 * @deprecated 1.19.7.2 — Заменена на enqueueCreateOrderWithItems (атомарный
 * RPC). Оставлена в коде для backward-compat с outbox-айтемами, уже
 * накопленными в Dexie у пилотов до апдейта.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function enqueueCreateOrder(order: WorkOrder, reason: string | null = null) {
  const existing = await db.outbox
    .where('order_id')
    .equals(order.id)
    .filter((item) => item.type === 'create_order')
    .first();

  const item: OutboxItem = {
    id: existing?.id ?? crypto.randomUUID(),
    type: 'create_order',
    order_id: order.id,
    payload: { ...order },
    created_at: existing?.created_at ?? new Date().toISOString(),
    status: 'pending',
    attempts: existing?.attempts ?? 0,
    last_error: reason,
    next_retry_at: null,
    locked_at: null,
  };

  await db.outbox.put(item);

  if (reason) {
    console.warn(`[Кворум sync] create_order queued for ${order.id}: ${reason}`);
  }
}

/**
 * 1.19.7.2 — Атомарный путь через RPC `create_order_with_items`.
 *
 * Записывает в outbox ОДИН элемент типа `create_order_with_items`, payload
 * содержит и order, и items[]. При flush'е outbox handler вызовет RPC
 * `create_order_with_items` — серверная функция с одной транзакцией, что
 * устраняет гонку RLS (parent и children видны вместе при проверке).
 *
 * Дедуп по order_id: если для этой заявки уже есть pending запись, мы
 * переиспользуем её id (это сохраняет attempts и created_at), просто
 * обновляем payload свежими данными. Так повторное нажатие «Создать»
 * при медленной сети не создаёт двойников в outbox.
 */
async function enqueueCreateOrderWithItems(
  order: WorkOrder,
  items: WorkOrderItem[],
  reason: string | null = null,
) {
  const existing = await db.outbox
    .where('order_id')
    .equals(order.id)
    .filter((it) => it.type === 'create_order_with_items')
    .first();

  const item: OutboxItem = {
    id: existing?.id ?? crypto.randomUUID(),
    type: 'create_order_with_items',
    order_id: order.id,
    payload: { order: { ...order }, items },
    created_at: existing?.created_at ?? new Date().toISOString(),
    status: 'pending',
    attempts: existing?.attempts ?? 0,
    last_error: reason,
    next_retry_at: null,
    locked_at: null,
  };

  await db.outbox.put(item);

  if (reason) {
    console.warn(`[Кворум sync] create_order_with_items queued for ${order.id}: ${reason}`);
  }
}

async function ensureDemoOrders() {
  const existing = await db.orders.count();
  if (existing > 0) return await db.orders.toArray();

  const now = new Date().toISOString();
  const date = todayYMD();
  const demoOrders: WorkOrder[] = [
    {
      id: 'demo-order-1',
      company_id: DEFAULT_COMPANY_ID,
      client_name: 'БЦ Северная Башня',
      client_phone: '+7 900 000-00-01',
      address: 'Москва, Тестовая улица, 10',
      floor: '12',
      location_kind: 'Офис',
      assigned_to: 'local-technician',
      created_by: 'local-dispatcher',
      work_type: 'maintenance',
      service_type_id: 'local-maintenance',
      checklist_template_id: PILOT_TEMPLATE_IDS.maintenance,
      priority: 'urgent',
      scheduled_date: date,
      time_from: '09:00',
      time_to: '10:30',
      status: 'assigned',
      started_at: null,
      completed_at: null,
      created_at: now,
      updated_at: now,
      description: 'Плановое ТО VRF-системы на 12 этаже.',
      failure_reason: null,
      result_summary: null,
    },
    {
      id: 'demo-order-2',
      company_id: DEFAULT_COMPANY_ID,
      client_name: 'Кафе Лето',
      client_phone: '+7 900 000-00-02',
      address: 'Москва, Парк-авеню, 7',
      floor: '1',
      location_kind: 'Ресторан',
      assigned_to: 'local-technician',
      created_by: 'local-dispatcher',
      work_type: 'diagnostics',
      service_type_id: 'local-diagnostics',
      checklist_template_id: PILOT_TEMPLATE_IDS.diagnostics,
      priority: 'high',
      scheduled_date: date,
      time_from: '12:00',
      time_to: '13:00',
      status: 'assigned',
      started_at: null,
      completed_at: null,
      created_at: now,
      updated_at: now,
      description: 'Диагностика протечки и вибрации внутреннего блока.',
      failure_reason: null,
      result_summary: null,
    },
    {
      id: 'demo-order-3',
      company_id: DEFAULT_COMPANY_ID,
      client_name: 'Склад Орбита',
      client_phone: '+7 900 000-00-03',
      address: 'Химки, Индустриальная, 4',
      floor: '2',
      location_kind: 'Склад',
      assigned_to: null,
      created_by: 'local-dispatcher',
      work_type: 'repair',
      service_type_id: 'local-repair',
      checklist_template_id: PILOT_TEMPLATE_IDS.repair,
      priority: 'normal',
      scheduled_date: date,
      time_from: '15:00',
      time_to: '17:00',
      status: 'new',
      started_at: null,
      completed_at: null,
      created_at: now,
      updated_at: now,
      description: 'Ремонт наружного блока после аварийной остановки.',
      failure_reason: null,
      result_summary: null,
    },
    {
      id: 'demo-order-4',
      company_id: DEFAULT_COMPANY_ID,
      client_name: 'Шоурум Nova',
      client_phone: '+7 900 000-00-04',
      address: 'Москва, Ленинский проспект, 88',
      floor: '1',
      location_kind: 'Шоурум',
      assigned_to: 'local-technician',
      created_by: 'local-dispatcher',
      work_type: 'installation',
      service_type_id: 'local-installation',
      checklist_template_id: PILOT_TEMPLATE_IDS.installation,
      priority: 'normal',
      scheduled_date: date,
      time_from: '17:30',
      time_to: '19:00',
      status: 'done',
      started_at: now,
      completed_at: now,
      created_at: now,
      updated_at: now,
      description: 'Первичный монтаж кассетного блока в шоуруме.',
      failure_reason: null,
      result_summary: {
        checklist_filled: 6,
        checklist_total: 6,
        photo_count: 3,
        has_signature: true,
        signature_refused: false,
        technician_note: 'Монтаж завершён, клиент проинструктирован.',
      },
    },
    {
      // Repeat visit — demonstrates follow-up / repeat_of flow
      id: 'demo-order-5',
      company_id: DEFAULT_COMPANY_ID,
      client_name: 'Склад Орбита',
      client_phone: '+7 900 000-00-03',
      address: 'Химки, Индустриальная, 4',
      floor: '2',
      location_kind: 'Склад',
      assigned_to: 'local-technician',
      created_by: 'local-dispatcher',
      work_type: 'repair',
      service_type_id: 'local-repair',
      checklist_template_id: PILOT_TEMPLATE_IDS.repair,
      priority: 'high',
      scheduled_date: date,
      time_from: '15:00',
      time_to: '17:00',
      status: 'assigned',
      started_at: null,
      completed_at: null,
      created_at: now,
      updated_at: now,
      description: 'Повторный выезд: проверка после ремонта наружного блока.',
      failure_reason: null,
      result_summary: null,
      repeat_of: 'demo-order-3',
    },
  ];

  await db.orders.bulkPut(demoOrders);
  return demoOrders;
}

export async function fetchOrdersForDispatcher(user: User): Promise<WorkOrder[]> {
  if (!isSupabaseConfigured) {
    const orders = await ensureDemoOrders();
    return sortOrders(orders.filter((o) => o.company_id === user.company_id));
  }

  const { data, error } = await supabase
    .from('work_orders')
    .select('*')
    .eq('company_id', user.company_id)
    .order('created_at', { ascending: false })
    .order('scheduled_date', { ascending: true })
    .order('time_from', { ascending: true });

  if (error) throw error;

  // P0-1 stage 2: attach items so detail screens can render the work composition.
  // P0-4: also hydrate helpers so the crew is visible in dispatcher/admin lists.
  const orders = await hydrateOrders(mapFetchedOrderRows(data));
  return await mergeFetchedOrders(orders);
}

export async function fetchOrdersForTechnician(userId: string, companyId: string): Promise<WorkOrder[]> {
  if (!isSupabaseConfigured) {
    await ensureDemoOrders();
    // Offline: lead match only — Dexie cache of work_order_assignees
    // would be needed for helper visibility, which we don't sync yet.
    const rows = await db.orders.where('assigned_to').equals(userId).toArray();
    return sortOrders(rows.filter((o) => o.company_id === companyId));
  }

  // P0-4 — technician sees orders where they are EITHER the lead
  // (work_orders.assigned_to) OR a helper (work_order_assignees row).
  // Two queries, dedupe by order id. We deliberately keep them separate
  // for clarity over building a complex Supabase RPC; the lists are small
  // (one technician's daily load).
  const leadsQuery = supabase
    .from('work_orders')
    .select('*')
    .eq('company_id', companyId)
    .eq('assigned_to', userId);

  // P0-4 — orders where this user is a helper. Two-step approach to avoid
  // depending on a PostgREST FK alias: first get the order ids from the
  // join table, then fetch those orders.
  const helperIdsQuery = supabase
    .from('work_order_assignees')
    .select('work_order_id')
    .eq('user_id', userId);

  const [leadsResult, helperIdsResult] = await Promise.all([leadsQuery, helperIdsQuery]);

  if (leadsResult.error) throw leadsResult.error;
  if (helperIdsResult.error) {
    // If the join table query fails (e.g. migration not applied), fall back
    // to lead-only mode so the technician at least sees their primary work.
    console.warn(
      `[Кворум] helper-side fetch failed (${helperIdsResult.error.message}). ` +
        'Showing only orders where this technician is the lead.',
    );
  }

  const leadOrders = mapFetchedOrderRows(leadsResult.data);

  // Pull the actual orders for the helper-side ids in one extra query.
  // We tenant-scope and dedupe with the leads list at the end anyway.
  let helpedOrders: WorkOrder[] = [];
  const helperOrderIds = (helperIdsResult.data ?? [])
    .map((row) => (isOrderRecord(row) ? String(row.work_order_id) : null))
    .filter((v): v is string => v !== null);
  if (helperOrderIds.length > 0) {
    const { data: helpedRaw, error: helpedErr } = await supabase
      .from('work_orders')
      .select('*')
      .in('id', helperOrderIds)
      .eq('company_id', companyId);
    if (helpedErr) {
      console.warn(
        `[Кворум] failed to load helper orders by id (${helpedErr.message}).`,
      );
    } else {
      helpedOrders = mapFetchedOrderRows(helpedRaw);
    }
  }

  // Dedupe — a tech might be both lead AND helper in pathological cases.
  // Lead always wins on the merge, helpers are dropped.
  const byId = new Map<string, WorkOrder>();
  for (const o of leadOrders) byId.set(o.id, o);
  for (const o of helpedOrders) if (!byId.has(o.id)) byId.set(o.id, o);
  const combined = Array.from(byId.values());

  const hydrated = await hydrateOrders(combined);
  const orders = await mergeFetchedOrders(hydrated, { technicianId: userId });

  // Final filter — only orders this tech is part of, tenant-isolated.
  return orders.filter(
    (order) =>
      order.company_id === companyId &&
      (order.assigned_to === userId ||
        (order.helpers ?? []).some((h) => h.user_id === userId)),
  );
}

export async function fetchOrderById(orderId: string, user?: User): Promise<WorkOrder | null> {
  if (!isSupabaseConfigured) {
    await ensureDemoOrders();
    const order = (await db.orders.get(orderId)) ?? null;
    if (order && user) assertTenantMatch(user, order.company_id);
    return order;
  }

  let byIdQuery = supabase.from('work_orders').select('*').eq('id', orderId);
  if (user) byIdQuery = byIdQuery.eq('company_id', user.company_id);
  const { data, error } = await byIdQuery.maybeSingle();
  if (error) throw error;
  if (!data) {
    const cached = (await db.orders.get(orderId)) ?? null;
    if (cached && user) assertTenantMatch(user, cached.company_id);
    return cached;
  }

  if (!isOrderRecord(data)) {
    throw new Error('Unexpected work order payload');
  }

  // P0-1 stage 2: hydrate items for single-order load too.
  // P0-4: also hydrate helpers.
  const [hydrated] = await hydrateOrders([mapOrder(data)]);
  const order = await mergeRemoteWithLocal(hydrated);
  if (user) assertTenantMatch(user, order.company_id);
  await db.orders.put(order);
  return order;
}

/**
 * Find any work orders that reference `parentOrderId` via `repeat_of`.
 * Used by the dispatcher's OrderDetail to decide whether the "Создать
 * повторный выезд" button should show, or whether to show a link to the
 * existing follow-up instead.
 *
 * Returns a lightweight projection (id + status + created_at) — full
 * order rows are read separately when the user navigates to the
 * follow-up. We rely on RLS to scope to tenant.
 *
 * Empty array if Supabase is not configured (demo mode just returns
 * empty so the button stays available).
 */
export async function fetchFollowUpOrdersFor(
  parentOrderId: string,
): Promise<Array<{ id: string; status: OrderStatus; created_at: string }>> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('work_orders')
    .select('id, status, created_at')
    .eq('repeat_of', parentOrderId)
    .order('created_at', { ascending: true });
  if (error) {
    // Non-fatal — if we can't read, just allow the button (worst case
    // the DB-level unique index will block the duplicate insert).
    return [];
  }
  return (data ?? []).map((r) => {
    const rec = r as Record<string, unknown>;
    return {
      id: String(rec.id),
      status: rec.status as OrderStatus,
      created_at: String(rec.created_at),
    };
  });
}

/**
 * P0-5 stage 2 — update the order's crew label. Pass null to clear the
 * crew anchor (switching to «свободное назначение»). This only touches
 * `work_orders.crew_id`; the actual lead and helpers must be updated via
 * `assignTechnician` + `setOrderHelpers` separately by the caller.
 *
 * Why a separate API call: the caller may want to update only the crew
 * anchor without touching assignees (e.g. lead trimmed helpers via
 * "Команда на выезд" but the crew badge stays).
 */
export async function updateOrderCrew(orderId: string, crewId: string | null): Promise<void> {
  // Local cache first — UI responds instantly.
  const order = await db.orders.get(orderId);
  if (order) {
    await db.orders.put({
      ...order,
      crew_id: crewId,
      updated_at: new Date().toISOString(),
    });
  }
  if (!isSupabaseConfigured) return;
  const { error } = await supabase
    .from('work_orders')
    .update({ crew_id: crewId })
    .eq('id', orderId);
  if (error) throw error;
}

/**
 * P0-3b — update the order's object_id (the equipment this order
 * targets). Pass null to clear. Same shape as updateOrderCrew.
 *
 * 1.17.1 — Offline-first. Локальный Dexie put сразу + outbox
 * link_object_to_order. Раньше прямой supabase.update падал в
 * оффлайне; теперь сохраняется надёжно и синхронизируется когда сеть
 * появится.
 */
export async function updateOrderObject(orderId: string, objectId: string | null): Promise<void> {
  const order = await db.orders.get(orderId);
  const previousObjectId = order?.object_id ?? null;
  if (order) {
    await db.orders.put({
      ...order,
      object_id: objectId,
      updated_at: new Date().toISOString(),
    });
  }
  if (!isSupabaseConfigured) return;

  if (navigator.onLine) {
    const { error } = await supabase
      .from('work_orders')
      .update({ object_id: objectId, updated_at: new Date().toISOString() })
      .eq('id', orderId);
    if (error) {
      // Если сеть упала между navigator.onLine и запросом — fallback на outbox.
      const { enqueueOutboxItem } = await import('../sync/outboxProcessor');
      await enqueueOutboxItem({
        type: 'link_object_to_order',
        order_id: orderId,
        payload: { object_id: objectId },
      });
    }
  } else {
    const { enqueueOutboxItem } = await import('../sync/outboxProcessor');
    await enqueueOutboxItem({
      type: 'link_object_to_order',
      order_id: orderId,
      payload: { object_id: objectId },
    });
  }

  // 1.19.0 — audit event на привязку объекта к заявке. Пишем только
  // если object_id фактически изменился (предотвращаем шум от ре-привязок).
  if (previousObjectId !== objectId) {
    void (async () => {
      const { useAuthStore } = await import('../auth/authStore');
      const { writeAuditEvent } = await import('../audit_log/api');
      void writeAuditEvent(useAuthStore.getState().user, {
        entity_type: 'work_order',
        entity_id: orderId,
        action: 'object_linked_to_order',
        payload: { from: previousObjectId, to: objectId },
      });
    })();
  }
}

/**
 * 1.10.0 — toggle "Не возвращаемся" flag on a failed order.
 *
 * Set to true to remove it from the dispatcher attention strip without
 * creating a follow-up. Set to false to bring it back if dispatcher
 * changed their mind.
 *
 * Doesn't affect status, archive visibility, or any history. Pure UI flag.
 */
export async function setOrderDismissedAttention(
  orderId: string,
  dismissed: boolean,
): Promise<void> {
  const order = await db.orders.get(orderId);
  if (order) {
    await db.orders.put({
      ...order,
      dismissed_attention: dismissed,
      updated_at: new Date().toISOString(),
    });
  }
  if (!isSupabaseConfigured) return;
  const { error } = await supabase
    .from('work_orders')
    .update({ dismissed_attention: dismissed })
    .eq('id', orderId);
  if (error) throw error;
}

// ── 1.13.0 — Workflow одобрения "не возвращаемся к заявке" ──────────────────
//
// Три функции на полное состояние:
//   • requestDismissal     — диспетчер создаёт запрос. status='pending'.
//   • approveDismissal     — админ одобряет. status='approved' +
//                             dismissed_attention=true.
//   • rejectDismissal      — админ отклоняет. status='rejected'.
//
// Все три функции:
//   • обновляют локальный Dexie сразу для оптимистичного UI;
//   • затем синхронизируют с Supabase. Если оффлайн / ошибка — локальное
//     состояние остаётся, но при следующем sync server может перетереть
//     (это нормально, политика last-write-wins сейчас и так в проекте).
//   • не используют outbox для упрощения первой итерации. Для запроса
//     закрытия оффлайн-сценарий маргинальный — диспетчер обычно за
//     столом со связью. Если станет важно — добавим outbox-обёртку.

interface DismissalRequestDb {
  reason: string;
  requested_by: string;
  requested_at: string;
  status: 'pending' | 'approved' | 'rejected';
  decided_by?: string | null;
  decided_at?: string | null;
  decision_note?: string | null;
}

export async function requestDismissal(
  orderId: string,
  requestedBy: string,
  reason: string,
): Promise<void> {
  const trimmed = reason.trim();
  if (trimmed.length < 10) {
    throw new Error('Причина должна содержать не менее 10 символов');
  }
  const ts = new Date().toISOString();
  const request: DismissalRequestDb = {
    reason: trimmed,
    requested_by: requestedBy,
    requested_at: ts,
    status: 'pending',
    decided_by: null,
    decided_at: null,
    decision_note: null,
  };
  const order = await db.orders.get(orderId);
  if (order) {
    await db.orders.put({
      ...order,
      dismissal_request: request,
      updated_at: ts,
    });
  }
  if (!isSupabaseConfigured) return;
  const { error } = await supabase
    .from('work_orders')
    .update({ dismissal_request: request, updated_at: ts })
    .eq('id', orderId);
  if (error) throw error;

  // 1.16.0 — audit. Ленивый импорт authStore чтобы не дёргать его при
  // каждом import api.ts (api грузится на холодном старте, store —
  // позже после auth flow).
  void (async () => {
    const { useAuthStore } = await import('../auth/authStore');
    const { writeAuditEvent } = await import('../audit_log/api');
    void writeAuditEvent(useAuthStore.getState().user, {
      entity_type: 'work_order',
      entity_id: orderId,
      action: 'order_dismissal_requested',
      payload: { reason: trimmed },
    });
  })();
}

export async function approveDismissal(
  orderId: string,
  decidedBy: string,
  decisionNote?: string | null,
): Promise<void> {
  const order = await db.orders.get(orderId);
  const current = order?.dismissal_request ?? null;
  if (!current || current.status !== 'pending') {
    throw new Error('Нет ожидающего запроса для одобрения');
  }
  const ts = new Date().toISOString();
  const updated: DismissalRequestDb = {
    ...current,
    status: 'approved',
    decided_by: decidedBy,
    decided_at: ts,
    decision_note: decisionNote?.trim() || null,
  };
  if (order) {
    await db.orders.put({
      ...order,
      dismissal_request: updated,
      dismissed_attention: true,
      updated_at: ts,
    });
  }
  if (!isSupabaseConfigured) return;
  const { error } = await supabase
    .from('work_orders')
    .update({
      dismissal_request: updated,
      dismissed_attention: true,
      updated_at: ts,
    })
    .eq('id', orderId);
  if (error) throw error;

  // 1.16.0 — audit.
  void (async () => {
    const { useAuthStore } = await import('../auth/authStore');
    const { writeAuditEvent } = await import('../audit_log/api');
    void writeAuditEvent(useAuthStore.getState().user, {
      entity_type: 'work_order',
      entity_id: orderId,
      action: 'order_dismissal_approved',
      payload: { note: decisionNote?.trim() || null },
    });
  })();
}

export async function rejectDismissal(
  orderId: string,
  decidedBy: string,
  decisionNote: string,
): Promise<void> {
  const trimmed = decisionNote.trim();
  if (trimmed.length < 5) {
    throw new Error('Объясните почему отказ (не менее 5 символов)');
  }
  const order = await db.orders.get(orderId);
  const current = order?.dismissal_request ?? null;
  if (!current || current.status !== 'pending') {
    throw new Error('Нет ожидающего запроса для отклонения');
  }
  const ts = new Date().toISOString();
  const updated: DismissalRequestDb = {
    ...current,
    status: 'rejected',
    decided_by: decidedBy,
    decided_at: ts,
    decision_note: trimmed,
  };
  if (order) {
    await db.orders.put({
      ...order,
      dismissal_request: updated,
      // dismissed_attention НЕ ставим — заявка возвращается в attention
      // у диспетчера. Если ранее было true (от старой версии) — оставляем
      // как есть, тут не наша зона ответственности.
      updated_at: ts,
    });
  }
  if (!isSupabaseConfigured) return;
  const { error } = await supabase
    .from('work_orders')
    .update({ dismissal_request: updated, updated_at: ts })
    .eq('id', orderId);
  if (error) throw error;

  // 1.16.0 — audit.
  void (async () => {
    const { useAuthStore } = await import('../auth/authStore');
    const { writeAuditEvent } = await import('../audit_log/api');
    void writeAuditEvent(useAuthStore.getState().user, {
      entity_type: 'work_order',
      entity_id: orderId,
      action: 'order_dismissal_rejected',
      payload: { note: trimmed },
    });
  })();
}

/**
 * P0-3b — fetch the work order history for one customer_object. Used
 * by ObjectProfile to render the equipment's service timeline.
 *
 * Returns a lightweight projection like fetchCustomerOrderHistory.
 */
export async function fetchOrdersByObjectId(objectId: string): Promise<
  Array<{
    id: string;
    scheduled_date: string;
    time_from: string;
    status: string;
    work_type: string;
    address: string;
  }>
> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from('work_orders')
    .select('id, scheduled_date, time_from, status, work_type, address')
    .eq('object_id', objectId)
    .order('scheduled_date', { ascending: false })
    .order('time_from', { ascending: false });
  if (error) return [];
  return (data ?? []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      id: String(row.id),
      scheduled_date: String(row.scheduled_date ?? ''),
      time_from: String(row.time_from ?? ''),
      status: String(row.status ?? ''),
      work_type: String(row.work_type ?? ''),
      address: String(row.address ?? ''),
    };
  });
}

export async function createOrder(payload: CreateOrderPayload, user: User): Promise<WorkOrder> {
  // Guard: prevent duplicate follow-ups on the same parent. The DB has a
  // partial unique index as the canonical enforcer, but checking here
  // gives the dispatcher a human-readable message instead of a generic
  // 23505. Race conditions (two dispatchers clicking at the same
  // millisecond) still get caught by the DB.
  if (payload.repeat_of && isSupabaseConfigured) {
    const existing = await fetchFollowUpOrdersFor(payload.repeat_of);
    if (existing.length > 0) {
      throw new Error(
        'По этой заявке уже создана повторная — откройте её вместо создания новой.',
      );
    }
  }

  const now = new Date().toISOString();
  const resolvedTemplateId =
    payload.checklist_template_id ?? getSeedTemplateByWorkType(payload.work_type)?.id ?? DEFAULT_TEMPLATE_ID;

  const orderId = makeId();

  // P0-1 stage 2: derive the items list. If the caller passed `payload.items`
  // (new multi-task CreateOrder.tsx flow), respect it verbatim. Otherwise
  // synthesise a single item from the legacy order-level fields so existing
  // callers — and any code path that hasn't been migrated yet — still produce
  // a fully populated work_order_items row server-side, matching what stage 1's
  // backfill did for historical data.
  const itemsDraft: CreateWorkOrderItemPayload[] =
    payload.items && payload.items.length > 0
      ? payload.items
      : [
          {
            work_type: payload.work_type,
            service_type_id: payload.service_type_id ?? null,
            checklist_template_id: resolvedTemplateId,
            title: '',
            price_planned: null,
            price_amount: 0,
            position: 0,
          },
        ];

  // Synthesised items get UUIDs here (client-side) so the offline path can
  // reference them in dependent records (invoice lines, checklist results
  // when stage 3 wires per-item checklists). Server table also generates
  // UUIDs by default — but we want stable IDs known to the client immediately.
  const items: WorkOrderItem[] = itemsDraft.map((draft, index) => ({
    id: crypto.randomUUID(),
    work_order_id: orderId,
    work_type: draft.work_type,
    service_type_id: draft.service_type_id ?? null,
    checklist_template_id: draft.checklist_template_id ?? null,
    title: draft.title ?? '',
    status: 'pending',
    outcome: null,
    outcome_reason: null,
    result_summary: null,
    price_planned: draft.price_planned ?? null,
    // price_amount starts as a copy of price_planned (or 0). Technician may
    // override at close time and write price_adjustment_reason.
    price_amount: draft.price_amount ?? draft.price_planned ?? 0,
    price_adjustment_reason: null,
    started_at: null,
    completed_at: null,
    position: draft.position ?? index,
    created_at: now,
    updated_at: now,
  }));

  // Legacy order-level fields are kept in sync with the FIRST item so the
  // existing technician UI (one-checklist flow, stage 3 will rewire it) and
  // any analytics still reading service_type_id directly off work_orders
  // continue to work unchanged.
  const primaryItem = items[0];

  const order: WorkOrder = {
    id: orderId,
    company_id: user.company_id,
    client_name: payload.client_name,
    client_phone: payload.client_phone,
    address: payload.address,
    floor: payload.floor,
    location_kind: payload.location_kind,
    customer_id: payload.customer_id ?? null,
    crew_id: payload.crew_id ?? null,
    object_id: payload.object_id ?? null,
    assigned_to: payload.assigned_to,
    created_by: user.id,
    work_type: payload.work_type,
    service_type_id: primaryItem.service_type_id ?? null,
    checklist_template_id: primaryItem.checklist_template_id ?? resolvedTemplateId,
    priority: payload.priority,
    scheduled_date: payload.scheduled_date,
    time_from: payload.time_from,
    time_to: payload.time_to,
    status: payload.assigned_to ? 'assigned' : 'new',
    started_at: null,
    completed_at: null,
    created_at: now,
    updated_at: now,
    description: payload.description,
    failure_reason: null,
    result_summary: null,
    repeat_of: payload.repeat_of ?? null,
    items,
  };

  // Persist locally first — UI shows the new order immediately regardless of
  // connectivity. The Dexie row carries the full WorkOrder INCLUDING items,
  // so any screen reading it will see the proper composition right away.
  await db.orders.put(order);

  if (!isSupabaseConfigured) {
    return order;
  }

  // 1.19.7.2 — Атомарное создание parent + items одним outbox-айтемом
  // через RPC create_order_with_items.
  //
  // История:
  // • 1.19.7 ввёл атомарный путь чтобы устранить гонку RLS, но RPC
  //   падал permanent'но с "column repeat_of is of type uuid but
  //   expression is of type text" — ошибка в касте в миграции.
  // • 1.19.7.1 откатил клиент на двухзаписную схему как hotfix.
  // • 1.19.7.2 — RPC исправлен (repeat_of, v_order_id, item id — все
  //   uuid), клиент снова идёт через атомарный путь.
  //
  // Преимущество: parent и children записываются в одной транзакции.
  // RLS политика work_order_items.insert видит parent сразу, гонка
  // read-after-write inconsistency устранена. Никаких ручных retry.
  //
  // Старые enqueueCreateOrder/enqueueCreateOrderItems сохранены в файле
  // как unused — для backward-compat с уже накопленными в Dexie на
  // устройствах пилотов outbox-айтемами старого формата.
  await enqueueCreateOrderWithItems(order, items, navigator.onLine ? null : 'offline');

  if (navigator.onLine) {
    try {
      await triggerOutboxFlush();
    } catch (error) {
      console.warn(
        `[Кворум sync] immediate create_order flush failed for ${order.id}: ${
          error instanceof Error ? error.message : 'unknown error'
        }`,
      );
    }
  }

  // 1.16.0 — audit. Записываем после успешного локального put;
  // если outbox ещё не отправил на сервер — audit-запись подождёт
  // окно онлайна (writeAuditEvent сам через supabase.insert).
  void (async () => {
    const { writeAuditEvent } = await import('../audit_log/api');
    void writeAuditEvent(user, {
      entity_type: 'work_order',
      entity_id: order.id,
      action: 'order_created',
      payload: {
        title: order.client_name ?? null,
        work_type: order.work_type,
        customer_id: order.customer_id ?? null,
        repeat_of: order.repeat_of ?? null,
      },
    });
  })();

  return order;
}

/**
 * @deprecated 1.19.7.2 — Заменена на enqueueCreateOrderWithItems. Оставлена
 * в коде для backward-compat с outbox-айтемами `create_order_items` уже
 * накопленными в Dexie у пилотов до апдейта.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function enqueueCreateOrderItems(
  orderId: string,
  items: WorkOrderItem[],
  reason: string | null,
) {
  // We never deduplicate by order_id here the way enqueueCreateOrder does —
  // a follow-up "rewrite all items" is a separate API call that won't go
  // through createOrder anyway. So a single create_order_items entry per
  // createOrder() call is correct.
  const item: OutboxItem = {
    id: crypto.randomUUID(),
    type: 'create_order_items',
    order_id: orderId,
    // Wrapping items in an object (not bare array) keeps payload shape
    // consistent with other outbox types and makes future schema additions
    // (e.g. "replace" vs "append" semantics) possible without a migration.
    payload: { items },
    created_at: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    last_error: reason,
    next_retry_at: null,
    locked_at: null,
  };
  await db.outbox.put(item);
  if (reason) {
    console.warn(`[Кворум sync] create_order_items queued for ${orderId}: ${reason}`);
  }
}

export async function assignTechnician(orderId: string, technicianId: string): Promise<void> {
  // 1.16.0 — для audit нужен предыдущий assigned_to. Читаем до патча.
  const existing = await db.orders.get(orderId);
  const previousAssignedTo = existing?.assigned_to ?? null;

  const patch = { assigned_to: technicianId, status: 'assigned' as OrderStatus, updated_at: new Date().toISOString() };
  // Always write locally first so the UI reflects the change immediately.
  await db.orders.update(orderId, patch);

  if (isSupabaseConfigured) {
    if (navigator.onLine) {
      const { error } = await supabase.from('work_orders').update(patch).eq('id', orderId);
      if (error) throw error;
    } else {
      // Offline: queue as status_change so outbox flushes it when connectivity returns.
      const { enqueueOutboxItem } = await import('../sync/outboxProcessor');
      await enqueueOutboxItem({ type: 'status_change', order_id: orderId, payload: patch });
    }
  }

  // 1.16.0 — audit. Пишем только если assigned_to реально поменялся
  // (повторное assign на того же — не событие).
  if (previousAssignedTo !== technicianId) {
    void (async () => {
      const { useAuthStore } = await import('../auth/authStore');
      const { writeAuditEvent } = await import('../audit_log/api');
      void writeAuditEvent(useAuthStore.getState().user, {
        entity_type: 'work_order',
        entity_id: orderId,
        action: 'order_reassigned',
        payload: {
          from_user_id: previousAssignedTo,
          to_user_id: technicianId,
        },
      });
    })();
  }
}

/**
 * P0-4 — replace the order's helper list with `helperUserIds`. Idempotent:
 * if a user is already in the list they stay; new ids are added; missing
 * ids are removed. The lead (work_orders.assigned_to) is intentionally
 * NOT one of these — that's a separate API (assignTechnician) and is
 * denormalised on the order itself.
 *
 * Currently online-only — the offline outbox doesn't yet model helper
 * changes. Dispatcher always has connectivity when assigning, so this
 * is acceptable for stage one of P0-4.
 */
export async function setOrderHelpers(orderId: string, helperUserIds: string[]): Promise<void> {
  // De-duplicate just in case the UI passes a list with repeats.
  const desired = Array.from(new Set(helperUserIds));

  // Update the local Dexie cache first — UI feedback is instant.
  const order = await db.orders.get(orderId);
  if (order) {
    const nextHelpers: WorkOrderHelper[] = desired.map((uid) => {
      const existing = (order.helpers ?? []).find((h) => h.user_id === uid);
      return existing ?? {
        id: makeId(),
        work_order_id: orderId,
        user_id: uid,
        user_name: null,
        created_at: new Date().toISOString(),
      };
    });
    await db.orders.put({
      ...order,
      helpers: nextHelpers,
      updated_at: new Date().toISOString(),
    });
  }

  if (!isSupabaseConfigured) return;

  // Server side: fetch current rows, compute diff, apply minimally so we
  // don't churn rows or trip RLS-restricted deletes for unchanged rows.
  const { data: current, error: readErr } = await supabase
    .from('work_order_assignees')
    .select('id, user_id')
    .eq('work_order_id', orderId);
  if (readErr) throw readErr;

  const currentRows = (current ?? []).map((r) => {
    const rec = r as Record<string, unknown>;
    return { id: String(rec.id), user_id: String(rec.user_id) };
  });
  const currentIds = new Set(currentRows.map((r) => r.user_id));

  const toAdd = desired.filter((uid) => !currentIds.has(uid));
  const toRemove = currentRows
    .filter((r) => !desired.includes(r.user_id))
    .map((r) => r.id);

  if (toAdd.length > 0) {
    const rows = toAdd.map((uid) => ({
      work_order_id: orderId,
      user_id: uid,
      role: 'helper' as const,
    }));
    const { error } = await supabase.from('work_order_assignees').insert(rows);
    if (error) throw error;
  }

  if (toRemove.length > 0) {
    const { error } = await supabase
      .from('work_order_assignees')
      .delete()
      .in('id', toRemove);
    if (error) throw error;
  }
}

export async function updateOrderStatus(orderId: string, status: OrderStatus, fields: Partial<WorkOrder> = {}, user?: User): Promise<void> {
  if (user) {
    const existing = await db.orders.get(orderId);
    if (existing) assertTenantMatch(user, existing.company_id);
  }

  const patch = { status, updated_at: new Date().toISOString(), ...fields };

  // Always write locally first.
  await db.orders.update(orderId, patch);

  if (isSupabaseConfigured) {
    if (navigator.onLine) {
      const { error } = await supabase.from('work_orders').update(patch).eq('id', orderId);
      if (error) throw error;
    } else {
      // Offline: enqueue so the outbox processor flushes when back online.
      const { enqueueOutboxItem } = await import('../sync/outboxProcessor');
      await enqueueOutboxItem({ type: 'status_change', order_id: orderId, payload: patch });
    }
  }
}

export function subscribeToOrderEvents(onChange: () => void): () => void {
  if (!isSupabaseConfigured) {
    return () => undefined;
  }

  let isActive = true;
  const triggerRefresh = () => {
    if (isActive) {
      onChange();
    }
  };

  const channel: RealtimeChannel = supabase
    .channel(`fieldsnap-orders-${crypto.randomUUID()}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'work_orders' }, triggerRefresh)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'visit_results' }, triggerRefresh)
    .subscribe();

  return () => {
    isActive = false;
    void supabase.removeChannel(channel);
  };
}


// ─── Staff domain re-exports ──────────────────────────────────────────────────
// Staff / member management has moved to src/domains/staff/api.ts.
// These re-exports preserve backward compatibility for any existing imports
// that have not yet been migrated to the new domain path.
// Remove when all consumers have been updated.
export type { AddEmployeeInput } from '../staff/api';
export {
  fetchAllMembers,
  addEmployee,
  updateEmployeeProfile,
  updateMemberRole,
  deactivateMember,
  reactivateMember,
} from '../staff/api';

// fetchTechnicians stays here — it serves the dispatcher's assignment flow
// (useTeam → AssignTech) and belongs to the orders operational context,
// not to admin staff management.
export async function fetchTechnicians(companyId: string): Promise<User[]> {
  const { fetchAllMembers: fetchAll } = await import('../staff/api');
  const all = await fetchAll(companyId, false);
  return all.filter((u) => u.company_role === 'technician');
}
